import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPinIcon, CalendarIcon, ChevronDownIcon, CheckIcon, SearchIcon, ClockIcon } from 'lucide-react';
import { SlideFrame } from '../SlideFrame';
import { BrowserFrame } from '../BrowserFrame';
import { Cursor } from '../Cursor';
import { useSequence } from '../../../hooks/useSequence';
import { DECK_IMAGES } from '../../../data/deck';
const EASE = [0.23, 1, 0.32, 1] as const;

/** Cursor waypoints across the booking form, in frame coordinates. */
const PATH = [{
  x: 520,
  y: 330
}, {
  x: 174,
  y: 132
}, {
  x: 214,
  y: 182
}, {
  x: 462,
  y: 132
}, {
  x: 502,
  y: 202
}, {
  x: 750,
  y: 132
}, {
  x: 792,
  y: 206
}, {
  x: 238,
  y: 184
}, {
  x: 462,
  y: 184
}, {
  x: 500,
  y: 232
}, {
  x: 750,
  y: 184
}];
const TASKS = [{
  label: 'Pick-up location',
  at: 2
}, {
  label: 'Pick-up date',
  at: 4
}, {
  label: 'Return date',
  at: 6
}, {
  label: 'Daily budget',
  at: 7
}, {
  label: 'Vehicle type',
  at: 9
}, {
  label: 'Search availability',
  at: 10
}];
function Field({
  icon,
  label,
  value,
  placeholder,
  focused
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
  placeholder: string;
  focused?: boolean;
}) {
  return <div className={`h-10 px-3 rounded-lg border flex items-center gap-2 transition-colors duration-200 ${focused ? 'border-primary-indigo/50 bg-primary-indigo/[0.03]' : 'border-slate-200 bg-white'}`} data-id="element-1825" data-mp="1o8yztwxaju7i">
      <span className="text-slate-400" data-id="element-1826" data-mp="221ons6wdr979">{icon}</span>
      <div className="min-w-0 flex-1" data-id="element-1827" data-mp="220dig2nadte1">
        <p className="text-[7px] uppercase tracking-[0.14em] text-slate-400" data-id="element-1828" data-mp="223ja9pnav9dg">{label}</p>
        <p className={`text-[10.5px] truncate ${value ? 'text-dark-navy font-medium' : 'text-slate-300'}`} data-id="element-1829" data-mp="26yosaddl03w9">
          {value ?? placeholder}
        </p>
      </div>
      <ChevronDownIcon className="w-3 h-3 text-slate-300 shrink-0" data-id="element-1830" data-mp="co1zbapwm88d" />
    </div>;
}
function Overlay({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.div className={`absolute z-30 rounded-xl bg-white border border-slate-200 shadow-large p-2 ${className}`} initial={{
    opacity: 0,
    y: -6,
    scale: 0.98
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} exit={{
    opacity: 0,
    y: -6,
    scale: 0.98
  }} transition={{
    duration: 0.18,
    ease: 'easeOut'
  }} data-id="element-1831" data-mp="9lx7keelivqd">
      {children}
    </motion.div>;
}
function Calendar({
  highlight
}: {
  highlight: number;
}) {
  return <div className="w-[168px]" data-id="element-1832" data-mp="shnac3ucrw5b">
      <div className="flex items-center justify-between px-1 pb-1.5" data-id="element-1833" data-mp="oj7qg8tkj3w4">
        <span className="text-[8px] font-semibold text-dark-navy" data-id="element-1834" data-mp="129taw1yo7lw4">September 2026</span>
        <ChevronDownIcon className="w-2.5 h-2.5 text-slate-300" data-id="element-1835" data-mp="12bnmgpo1y1nj" />
      </div>
      <div className="grid grid-cols-7 gap-[3px]" data-id="element-1836" data-mp="xb9ltsos5tzj">
        {Array.from({
        length: 28
      }).map((_, index) => <span key={index} className={`h-[16px] rounded flex items-center justify-center text-[7px] ${index + 1 === highlight ? 'bg-primary-indigo text-white font-semibold' : 'text-slate-400'}`} data-id="element-1837" data-mp="1c5wcn6pm1iij" data-mpi={index}>
            {index + 1}
          </span>)}
      </div>
    </div>;
}
export function SlideCarRental({
  active
}: {
  active: boolean;
}) {
  const step = useSequence(PATH.map((_, index) => index === 0 ? 700 : index === 1 ? 700 : 720), active);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    setElapsed(0);
    if (!active) return;
    const timer = window.setInterval(() => setElapsed(value => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  const position = PATH[Math.min(step, PATH.length - 1)];
  const done = step >= 10;
  return <SlideFrame eyebrow="Traditional experience" title="A simple request becomes a long process" footnote={<div className="flex items-center justify-between" data-id="element-1839" data-mp="t6nyppkcagju">
          <p className="text-[14px] text-slate-500" data-id="element-1840" data-mp="t65r5ws88fwm">
            Too many steps before the customer gets an answer.
          </p>
          <p className="text-[12px] text-slate-400 italic" data-id="element-1841" data-mp="1riru5pne8fc9">
            “I want to rent a car for AED 100 per day for 7 days starting tomorrow.”
          </p>
        </div>} data-id="element-1838" data-mp="1330quj08kmch">
      <div className="h-full flex gap-7" data-id="element-1842" data-mp="1mpoz1d2ywxty">
        <div className="relative flex-1 min-w-0" data-id="element-1843" data-mp="1htqfc2sl43zv">
          <BrowserFrame url="driveline-rentals.ae/booking" className="h-full" data-id="element-1844" data-mp="1hquhsfknfhby">
            <div className="relative flex flex-col h-full" data-id="element-1845" data-mp="1cvf5vt67bvi6">
              <div className="flex items-center gap-4 h-10 px-5 shrink-0 border-b border-slate-100" data-id="element-1846" data-mp="18rqvz3u3kntr">
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-dark-navy" data-id="element-1847" data-mp="13se1bwwzicgm">
                  Driveline
                </span>
                {['Cars', 'Deals', 'Locations', 'Support'].map(item => <span key={item} className="text-[9.5px] text-slate-400" data-id="element-1848" data-mp="2y6ops49o34lv">
                    {item}
                  </span>)}
                <span className="ml-auto px-2.5 py-1 rounded-md bg-slate-100 text-[9px] text-slate-500" data-id="element-1849" data-mp="30rmpbmrvossp">
                  Sign in
                </span>
              </div>

              <div className="m-5 p-4 rounded-xl border border-slate-200 bg-slate-50/50" data-id="element-1850" data-mp="35omwy24dm3lx">
                <div className="grid grid-cols-3 gap-3" data-id="element-1851" data-mp="3ai7w3xhph3mp">
                  <Field icon={<MapPinIcon className="w-3.5 h-3.5" data-id="element-1853" data-mp="3ffzqqz9qj3ul" />} label="Pick-up location" placeholder="Select a city" value={step >= 2 ? 'Dubai Marina' : undefined} focused={step === 1} data-id="element-1852" data-mp="3fgsm7438u6xf" />
                  <Field icon={<CalendarIcon className="w-3.5 h-3.5" data-id="element-1855" data-mp="2rpna900fbyse" />} label="Pick-up date" placeholder="Add date" value={step >= 4 ? 'Sun, 13 Sep' : undefined} focused={step === 3} data-id="element-1854" data-mp="2rpwwqdm9fntc" />
                  <Field icon={<CalendarIcon className="w-3.5 h-3.5" data-id="element-1857" data-mp="c1wx7sftb1uj" />} label="Return date" placeholder="Add date" value={step >= 6 ? 'Sun, 20 Sep' : undefined} focused={step === 5} data-id="element-1856" data-mp="byhhqoi87qsl" />
                </div>

                <div className="grid grid-cols-3 gap-3 mt-3" data-id="element-1858" data-mp="2h2u3kb4qenrf">
                  <div className="h-10 px-3 rounded-lg border border-slate-200 bg-white flex flex-col justify-center" data-id="element-1859" data-mp="2d337pt0clhg1">
                    <div className="flex items-center justify-between" data-id="element-1860" data-mp="1pcz3bjfc5dnz">
                      <p className="text-[7px] uppercase tracking-[0.14em] text-slate-400" data-id="element-1861" data-mp="1ke5rdvfp5upx">
                        Daily budget
                      </p>
                      <p className="text-[9px] font-semibold text-dark-navy" data-id="element-1862" data-mp="1y6lxj53wa8v2">
                        {step >= 7 ? 'AED 100' : 'AED 1,200'}
                      </p>
                    </div>
                    <div className="relative h-1 mt-1.5 rounded-full bg-slate-100" data-id="element-1863" data-mp="15mdju5m59kyr">
                      <motion.div className="absolute inset-y-0 left-0 rounded-full bg-primary-indigo" animate={{
                      width: step >= 7 ? '18%' : '100%'
                    }} transition={{
                      duration: 0.5,
                      ease: EASE
                    }} data-id="element-1864" data-mp="11mmn7l9xsamb" />
                      <motion.span className="absolute -top-[3px] w-2.5 h-2.5 rounded-full bg-white border-2 border-primary-indigo" animate={{
                      left: step >= 7 ? '16%' : '94%'
                    }} transition={{
                      duration: 0.5,
                      ease: EASE
                    }} data-id="element-1865" data-mp="hmblzp0umkc1" />
                    </div>
                  </div>
                  <Field icon={<SearchIcon className="w-3.5 h-3.5" data-id="element-1867" data-mp="10aqbg7vipqaq" />} label="Vehicle type" placeholder="Any type" value={step >= 9 ? 'Economy' : undefined} focused={step === 8} data-id="element-1866" data-mp="10broghw3gmv3" />
                  <button type="button" className={`h-10 rounded-lg text-[11px] font-semibold text-white transition-colors duration-200 ${step >= 10 ? 'bg-dark-navy' : 'bg-primary-indigo'}`} data-id="element-1868" data-mp="3q13gj4cmlowl">
                    {step >= 10 ? 'Searching…' : 'Search'}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 px-5 shrink-0" data-id="element-1869" data-mp="2tbb8r8dj01nj">
                {['Automatic', 'Free cancellation', 'Unlimited km', 'Sort: Price'].map(chip => <span key={chip} className="px-2 py-1 rounded-md border border-slate-200 text-[8.5px] text-slate-400" data-id="element-1870" data-mp="3l0xnjjsqgy46">
                    {chip}
                  </span>)}
              </div>

              <div className="flex-1 min-h-0 px-5 pt-3 pb-4" data-id="element-1871" data-mp="3c7b5ewhg7tdg">
                {done ? <div className="space-y-2" data-id="element-1872" data-mp="cnqqa1unnql6">
                    {[{
                  title: 'Economy · Compact sedan',
                  price: 'AED 98 / day',
                  image: DECK_IMAGES.carSedan
                }, {
                  title: 'Economy · Hatchback',
                  price: 'AED 104 / day',
                  image: DECK_IMAGES.carHatch
                }].map((row, index) => <motion.div key={row.title} className="flex items-center gap-3 p-2 rounded-lg border border-slate-200" initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.35,
                  ease: EASE,
                  delay: index * 0.1
                }} data-id="element-1873" data-mp="1zhua78jrj70t" data-mpi={index}>
                        <img src={row.image} alt="" className="w-14 h-9 rounded object-cover bg-slate-100" data-id="element-1874" data-mp="2j5t8vl11ynpv" />
                        <div className="flex-1" data-id="element-1875" data-mp="2s7sr2gnszxue">
                          <p className="text-[10px] font-semibold text-dark-navy" data-id="element-1876" data-mp="2s4wtwujs5d70">{row.title}</p>
                          <p className="text-[8.5px] text-slate-400" data-id="element-1877" data-mp="2x3zzqh7l3ng1">
                            7 days · Dubai Marina · deposit required
                          </p>
                        </div>
                        <p className="text-[10px] font-bold text-dark-navy" data-id="element-1878" data-mp="1mf22zip2lfs">{row.price}</p>
                        <span className="px-2 py-1 rounded bg-slate-100 text-[8.5px] text-slate-500" data-id="element-1879" data-mp="5otk5hejhh60">
                          Select
                        </span>
                      </motion.div>)}
                  </div> : <div className="h-full rounded-lg border border-dashed border-slate-200 flex items-center justify-center" data-id="element-1880" data-mp="kdy3x30zftg8">
                    <p className="text-[10px] text-slate-300" data-id="element-1881" data-mp="pelwht4v0ick">
                      Complete every field above to see availability
                    </p>
                  </div>}
              </div>

              <AnimatePresence data-id="element-1882" data-mp="2r7jtm15o5x3b">
                {step === 1 && <Overlay key="loc" className="left-[36px] top-[156px] w-[190px]" data-id="element-1883" data-mp="2v7ty19dtcfg2">
                    {['Dubai Marina', 'Downtown Dubai', 'Deira', 'Airport T3'].map(option => <p key={option} className="px-2 py-1.5 rounded-md text-[9.5px] text-slate-600 hover:bg-slate-50" data-id="element-1884" data-mp="2iwkvc6jqsm3e">
                        {option}
                      </p>)}
                  </Overlay>}
                {step === 3 && <Overlay key="pickup" className="left-[324px] top-[156px]" data-id="element-1885" data-mp="36k1bkatqgqdz">
                    <Calendar highlight={13} data-id="element-1886" data-mp="31o3u216qhdzm" />
                  </Overlay>}
                {step === 5 && <Overlay key="return" className="left-[612px] top-[156px]" data-id="element-1887" data-mp="2xnjuys2lo0au">
                    <Calendar highlight={20} data-id="element-1888" data-mp="2smcmsrwjoz3n" />
                  </Overlay>}
                {step === 8 && <Overlay key="type" className="left-[324px] top-[208px] w-[190px]" data-id="element-1889" data-mp="1h7h2oqf4q5fp">
                    {['Economy', 'SUV', 'Luxury', 'Van'].map(option => <p key={option} className="px-2 py-1.5 rounded-md text-[9.5px] text-slate-600" data-id="element-1890" data-mp="1ipfov7hr8th1">
                        {option}
                      </p>)}
                  </Overlay>}
              </AnimatePresence>
            </div>
          </BrowserFrame>

          <Cursor x={position.x} y={position.y} clickId={step} visible={step > 0} data-id="element-1891" data-mp="zx3o2p6r2090" />
        </div>

        <div className="w-[196px] shrink-0 flex flex-col" data-id="element-1892" data-mp="14pwt7e64ralm">
          <p className="text-[9.5px] font-semibold uppercase tracking-[0.2em] text-slate-400" data-id="element-1893" data-mp="pzz0qm163yqm">
            Steps required
          </p>
          <ul className="mt-4 space-y-2" data-id="element-1894" data-mp="uya72ms97r43">
            {TASKS.map(task => {
            const complete = step >= task.at;
            return <li key={task.label} className="flex items-center gap-2.5" data-id="element-1895" data-mp="h1n15uibpqm1">
                  <motion.span className="w-4 h-4 rounded-full flex items-center justify-center shrink-0" animate={{
                backgroundColor: complete ? '#0B1220' : '#E2E8F0'
              }} transition={{
                duration: 0.25
              }} data-id="element-1896" data-mp="lxkh3zpocuya">
                    <CheckIcon className="w-2.5 h-2.5 text-white" strokeWidth={3} data-id="element-1897" data-mp="be1l4xr5huuo" />
                  </motion.span>
                  <motion.span className="text-[12px]" animate={{
                color: complete ? '#0B1220' : '#94A3B8'
              }} transition={{
                duration: 0.25
              }} data-id="element-1898" data-mp="7adechacf3ah">
                    {task.label}
                  </motion.span>
                </li>;
          })}
          </ul>

          <div className="mt-auto space-y-2" data-id="element-1899" data-mp="q1momsrxtw34">
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200" data-id="element-1900" data-mp="q2o9tq2o3yyk">
              <ClockIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-1901" data-mp="1427bc00z4c1w" />
              <span className="text-[11px] text-slate-500" data-id="element-1902" data-mp="z3vx7d3nbfdv">
                On screen · {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}
              </span>
            </div>
            <motion.div className="px-3 py-2.5 rounded-xl bg-error/[0.06] border border-error/20" animate={{
            opacity: done ? 1 : 0.35
          }} transition={{
            duration: 0.4
          }} data-id="element-1903" data-mp="2yhcrg733fufr">
              <p className="font-display font-bold text-[15px] text-error leading-none" data-id="element-1904" data-mp="379y5xazozeys">
                ~2m 40s
              </p>
              <p className="mt-1 text-[10px] text-error/80" data-id="element-1905" data-mp="2amjqnh5rxo2c">
                average time a real customer spends before a first result
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </SlideFrame>;
}